import React, { useEffect, useState } from "react";
import { Input, Button, message } from "antd";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import { UTCToJapanTime } from "common/helper/time.helper";
import { useSelector } from "react-redux";
import { RootState } from "store/store";

type ChatProps = {
  buyerId: number | undefined;
  sellerId: number | undefined;
  productId: number | undefined;
};

type ChatMessageType = {
  id: number;
  content: string;
  conversation_id: number;
  send_at: string;
  sender_name: string;
  sender_type: number;
};

const Chat: React.FC<ChatProps> = ({ buyerId, sellerId, productId }) => {
  const [chatData, setChatData] = useState<any>(null);
  const [messageText, setMessageText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { userData } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const fetchChatMessages = () => {
      if (buyerId && productId) {
        axiosInstance
          .get(API_ENDPOINTS.GET_CHAT_MESSAGE, {
            params: {
              product_id: productId,
              buyer_id: buyerId,
              seller_id: sellerId,
            },
          })
          .then((response) => {
            setChatData(response.data);
          })
          .catch((error) => {
            console.error("Error fetching chat messages:", error);
          });
      }
    };

    fetchChatMessages();
    const intervalId = setInterval(fetchChatMessages, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [buyerId, productId]);

  const sendMessage = async () => {
    if (!messageText.trim()) {
      messageApi.warning("内容を入力してください");
      return;
    }

    if (!buyerId || !productId) {
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.put(API_ENDPOINTS.CHAT, {
        product_id: productId,
        buyer_id: buyerId,
        seller_id: sellerId,
        content: messageText,
      });

      setMessageText("");

      if (buyerId && productId) {
        axiosInstance
          .get(API_ENDPOINTS.GET_CHAT_MESSAGE, {
            params: {
              product_id: productId,
              buyer_id: buyerId,
            },
          })
          .then((response) => {
            setChatData(response.data);
          });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      messageApi.error("Send message error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderChatHtml = (data: any) => {
    if (data.data) {
      return data.data.messages.map((messageData: ChatMessageType) => {
        const timeJapan = UTCToJapanTime(messageData.send_at);
        if (messageData.sender_type === userData.type) {
          return (
            <div
              key={messageData.id}
              className="self-stretch bg-white flex flex-col justify-start items-end gap-2.5"
            >
              {primaryChatHtml(
                messageData.sender_name,
                messageData.content,
                `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`,
              )}
            </div>
          );
        }

        return (
          <div
            key={messageData.id}
            className="self-stretch bg-white flex flex-col justify-start items-start gap-2.5"
          >
            {secondaryChatHtml(
              messageData.sender_name,
              messageData.content,
              `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`,
            )}
          </div>
        );
      });
    }
  };

  const primaryChatHtml = (
    companyName: string,
    message: string,
    time: string,
  ) => {
    return (
      <>
        <div className="text-right justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
          {companyName}
        </div>
        <div className="w-full md:w-[660px] p-5 bg-primary-light rounded-tl-xl rounded-bl-xl rounded-br-xl flex flex-col justify-start items-start gap-3.5 overflow-hidden">
          <div
            style={{ whiteSpace: "pre-wrap" }}
            className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
          >
            {message}
          </div>
          <div className="self-stretch text-right justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
            {time}
          </div>
        </div>
      </>
    );
  };

  const secondaryChatHtml = (
    companyName: string,
    message: string,
    time: string,
  ) => {
    return (
      <>
        <div className="text-right justify-start text-black text-[15px] md:text-base font-bold font-['Noto_Sans_JP'] leading-relaxed">
          {companyName}
        </div>
        <div className="w-full md:w-[660px] p-5 bg-[#F5F5F5] rounded-tr-xl rounded-bl-xl rounded-br-xl flex flex-col justify-start items-start gap-3.5 overflow-hidden">
          <div
            style={{ whiteSpace: "pre-wrap" }}
            className="self-stretch justify-start text-black text-[15px] md:text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
          >
            {message}
          </div>
          <div className="self-stretch text-right justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
            {time}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="self-stretch flex flex-col justify-center items-center gap-10">
      {contextHolder}
      <div className="self-stretch flex flex-col justify-start items-start gap-7">
        {/*title*/}
        <div className="self-stretch justify-start text-black text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
          メッセージ
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-7">
          {chatData && renderChatHtml(chatData)}
        </div>
      </div>

      {/*form chat*/}
      <div className="self-stretch flex flex-col justify-start items-start gap-[5px]">
        <Input.TextArea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="チャットを送る"
          className="self-stretch h-48 p-5 bg-white rounded-md border border-neutral-border resize-none text-base font-normal font-['Noto_Sans_JP'] leading-relaxed"
          maxLength={1000}
          showCount={{
            formatter: ({ count, maxLength }) => `${count} / ${maxLength}`,
          }}
        />
      </div>
      <div className="self-stretch flex flex-col justify-start items-center gap-2.5">
        <Button
          type="primary"
          loading={isLoading}
          onClick={sendMessage}
          disabled={!messageText.trim()}
          className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md text-[16px] md:text-lg font-bold font-['Noto_Sans_JP'] leading-7"
        >
          送信する
        </Button>
        <div className="self-stretch text-center justify-start text-black text-[11px] md:text-xs font-normal font-['Noto_Sans_JP'] leading-tight">
          相手のことを考え丁寧なコメントを心がけましょう。
          <br />
          不快な言葉遣いなどは利用制限や退会処分となることがあります。
        </div>
      </div>
      {/*end chat*/}
    </div>
  );
};

export default Chat;
